import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { useTranslation } from '@mc/i18n'

import { EmailStep } from '../LoginPage/EmailStep'
import { useCheckEmail } from '../../hooks/auth'
import { useCheckoutData } from '../../components/CheckoutProvider/CheckoutProvider'
import { paths } from '../../utils/paths'
import { useCtaPreserveQueryParams } from '../../utils/cta'
import { useNonCommerceGoldStandardEvents } from '../../hooks/tracking/useNonCommerceGoldStandardEvents'
import {
  MAX_EMAIL_LENGTH,
  MAX_EMAIL_LOCAL_PART_LENGTH,
} from '../../utils/validations'

import { CheckoutWrapper } from './CheckoutWrapper'

interface FormValues {
  email?: string
}

export const SignUpPage = ({
  errorMessage,
  emailCookie,
}: {
  errorMessage?: string
  emailCookie: string | null
}) => (
  <CheckoutWrapper emailCookie={emailCookie}>
    <SignUpContent errorMessage={errorMessage} />
  </CheckoutWrapper>
)

const SignUpContent = ({ errorMessage }: { errorMessage?: string }) => {
  const { t } = useTranslation('@mc/persona')
  const { push } = useRouter()
  const getUrlWithQueryParams = useCtaPreserveQueryParams()
  const { trackGoldStandardPageViewed, trackCartEmailCaptured } =
    useNonCommerceGoldStandardEvents('login')
  const [emailError, setEmailError] = useState<string | undefined>(undefined)

  // Gold Standard
  useEffect(() => {
    trackGoldStandardPageViewed()
  }, [trackGoldStandardPageViewed])

  const { checkEmail, checkEmailMutation, isLoadingCheckEmail } = useCheckEmail(
    {
      onSuccess: (res) => {
        if (
          res.identityProviders?.length === 1 &&
          res.identityProviders.includes('email')
        ) {
          push(
            getUrlWithQueryParams(paths.personaCustomAuth('login'), {
              from: 'signup',
              social: 'false',
              email,
              step: 'password',
            }),
          )
        }
      },
      onError: (error: { status: number }) => {
        if (error?.status === 404) {
          trackCartEmailCaptured({ email })
          push(getUrlWithQueryParams(paths.personaCheckoutMembership))
        } else if (error?.status === 400) {
          setEmailError(
            t('auth.login.emailFormatError', {
              maxLength: MAX_EMAIL_LENGTH,
              maxLocal: MAX_EMAIL_LOCAL_PART_LENGTH,
            }),
          )
        }
      },
    },
  )
  const { email, setEmail } = useCheckoutData()

  const handleEmailSubmit = ({ email: submitEmail }: FormValues) => {
    if (submitEmail) {
      setEmailError(undefined)
      setEmail(submitEmail)
      checkEmailMutation({ email: submitEmail })
    }
  }

  return (
    <EmailStep
      onEmailSubmit={handleEmailSubmit}
      showErrorBox={true}
      onChangeEmail={() => setEmailError(undefined)}
      emailError={emailError}
      providers={checkEmail?.identityProviders}
      isLoading={isLoadingCheckEmail}
      initialValues={{ email }}
      errorMessage={errorMessage}
    />
  )
}
